import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, ChevronRight, Info, OctagonAlert } from "lucide-react";

import { Toggle } from "nazare-ui/components/toggle";
import { JsonPreview } from "nazare-ui/components/preview";

import { useWorkflowRunLogs, useWorkflowRunLogsStream, type LogLine } from "../../../../workflows-api";
import { formatTimestamp } from "./run-detail-format";
import styles from "./run-detail.module.css";

/**
 * The Logs tab — ported from nazare-frontend's
 * `screens/admin/workflows/logs-tab.tsx`. A run's worker logs streamed from
 * CloudWatch: historical lines paint from the cursor-paginated `/logs`
 * endpoint (paged in via infinite scroll as the bottom of the log
 * scroll-container approaches view), and for a live run, new lines append
 * over `/logs/stream` SSE, de-duped by `eventId` against the historical
 * page. A tab-level Raw toggle flips every row from the parsed view to the
 * verbatim log line.
 *
 * Unlike the core app (which manages the live-lines subscription by hand),
 * this uses platform's own `useWorkflowRunLogsStream` hook, which already
 * encapsulates that state — see `../../../../workflows-api.ts`.
 */

type LogLevel = "info" | "warn" | "error";

/** Map a pino numeric level to a coarse severity (>=50 error, >=40 warn, else info). */
function levelOf(level: number | null | undefined): LogLevel {
  if (level == null) return "info";
  if (level >= 50) return "error";
  if (level >= 40) return "warn";
  return "info";
}

/** Level pill in the diagnostic colour vocabulary (red error / amber warn / grey info). */
function LevelPill({ level }: { level: number | null | undefined }) {
  const sev = levelOf(level);
  const className =
    sev === "error"
      ? "bg-[var(--red-bg)] text-[var(--red)] border-[var(--red)]"
      : sev === "warn"
        ? "bg-[var(--orange-bg)] text-[var(--orange)] border-[var(--orange)]"
        : "bg-[var(--card-2)] text-[var(--nzr-muted)] border-[var(--nzr-border)]";
  const Icon = sev === "error" ? OctagonAlert : sev === "warn" ? AlertTriangle : Info;
  const label = sev === "error" ? "Error" : sev === "warn" ? "Warn" : "Info";
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border shrink-0 ${className}`}
    >
      <Icon size={10} />
      {label}
    </span>
  );
}

function hasFields(fields: LogLine["fields"]): boolean {
  return !!fields && Object.keys(fields).length > 0;
}

/** A single log row: timestamp · level pill · msg, with a per-row fields expander. */
function LogRow({ line, raw }: { line: LogLine; raw: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const fieldsShown = hasFields(line.fields);

  if (raw) {
    return (
      <div className="px-3 py-1.5 border-t border-[var(--border-soft)] first:border-t-0">
        <JsonPreview json={line.raw || ""} />
      </div>
    );
  }

  return (
    <div className="border-t border-[var(--border-soft)] first:border-t-0">
      <div className="flex items-start gap-2 px-3 py-1">
        {fieldsShown ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="shrink-0 h-5 flex items-center text-[var(--nzr-muted)] hover:text-[var(--ink)]"
            aria-label={expanded ? "Hide fields" : "Show fields"}
          >
            <ChevronRight size={12} className={`transition-transform ${expanded ? "rotate-90" : ""}`} />
          </button>
        ) : (
          <span className="shrink-0 w-3" />
        )}
        <span className="shrink-0 h-5 flex items-center text-[11px] font-mono text-[var(--nzr-muted)] tabular-nums">
          {formatTimestamp(new Date(line.timestamp).toISOString())}
        </span>
        <span className="shrink-0 h-5 flex items-center">
          <LevelPill level={line.level} />
        </span>
        <span className="flex-1 min-w-0 text-[12px] font-mono text-[var(--ink)] whitespace-pre-wrap break-words leading-5">
          {line.msg || ""}
        </span>
      </div>
      {fieldsShown && expanded && (
        <div className="mx-3 mb-1.5 ml-8">
          <JsonPreview json={JSON.stringify(line.fields)} />
        </div>
      )}
    </div>
  );
}

export function LogsTab({ runId, source, status }: { runId: string; source: string; status: string }) {
  const isLive = status === "running" || status === "paused";
  const [raw, setRaw] = useState(false);

  const query = useWorkflowRunLogs(runId, source);
  const historical = useMemo<LogLine[]>(() => (query.data?.pages ?? []).flatMap((p) => p.lines), [query.data]);
  const logGroupConfigured = query.data?.pages?.[0]?.logGroupConfigured;

  const streamed = useWorkflowRunLogsStream(runId, isLive, source);
  const configured = logGroupConfigured ?? streamed.logGroupConfigured;

  // Merge historical + live, de-duping by eventId (a line can appear in both
  // the initial historical page and the live stream).
  const lines = useMemo<LogLine[]>(() => {
    if (streamed.lines.length === 0) return historical;
    const seen = new Set(historical.map((l) => l.eventId));
    return [...historical, ...streamed.lines.filter((l) => !seen.has(l.eventId))];
  }, [historical, streamed.lines]);

  // Infinite scroll: a sentinel near the bottom of the log scroll-container
  // pulls the next history page as it approaches view, so older lines
  // stream in on scroll rather than behind a button.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const pinnedToBottomRef = useRef(true);
  const previousRunRef = useRef({ runId, source });
  const { hasNextPage, isFetchingNextPage, fetchNextPage, isError } = query;
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || isError) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) void fetchNextPage();
      },
      { root: rootRef.current ?? null, rootMargin: "0px 0px 200px 0px" },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isError]);

  const viewportReady = !query.isLoading && !query.isError && configured !== false && lines.length > 0;

  // A new run or log source starts at the newest available line. Keep this
  // separate from the data effect so delayed initial data cannot reset a
  // user's choice to read older lines.
  useLayoutEffect(() => {
    const previous = previousRunRef.current;
    if (previous.runId === runId && previous.source === source) return;
    previousRunRef.current = { runId, source };
    pinnedToBottomRef.current = true;
  }, [runId, source]);

  useEffect(() => {
    const root = rootRef.current;
    if (!viewportReady || !root) return;

    const handleScroll = () => {
      pinnedToBottomRef.current = root.scrollHeight - root.scrollTop - root.clientHeight <= 8;
    };
    root.addEventListener("scroll", handleScroll, { passive: true });
    root.scrollTop = root.scrollHeight;
    const resizeObserver = new ResizeObserver(() => {
      if (pinnedToBottomRef.current) root.scrollTop = root.scrollHeight;
    });
    resizeObserver.observe(root);
    if (contentRef.current) resizeObserver.observe(contentRef.current);
    return () => {
      root.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
    };
  }, [viewportReady]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!viewportReady || !root || !pinnedToBottomRef.current) return;
    root.scrollTop = root.scrollHeight;
  }, [lines.length, raw, viewportReady, runId, source]);

  if (query.isLoading) {
    return <div className={`${styles.surface} p-4 text-[13px] text-[var(--nzr-muted)]`}>Loading logs…</div>;
  }
  if (query.isError) {
    return <div className={`${styles.surface} p-4 text-[13px] text-[var(--red)]`}>Failed to load logs.</div>;
  }
  if (configured === false) {
    return (
      <section className={`${styles.surface} p-4 text-[13px] text-[var(--nzr-muted)]`}>
        Log streaming not configured for this environment.
      </section>
    );
  }

  return (
    <section className={styles.surface}>
      <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-[var(--border-soft)]">
        <span className="text-[12px] text-[var(--nzr-muted)] tabular-nums">
          {lines.length.toLocaleString("en-GB")} line{lines.length === 1 ? "" : "s"}
          {isLive && <span className="ml-2 text-[var(--green)]">· live</span>}
        </span>
        <Toggle size="sm" variant="outline">
          <Toggle.Item active={!raw} onClick={() => setRaw(false)}>
            Parsed
          </Toggle.Item>
          <Toggle.Item active={raw} onClick={() => setRaw(true)}>
            Raw
          </Toggle.Item>
        </Toggle>
      </div>

      {lines.length === 0 ? (
        <div className="p-4 text-[13px] text-[var(--nzr-muted)]">No logs recorded for this run yet.</div>
      ) : (
        <div ref={rootRef} className="max-h-[70vh] overflow-y-auto">
          <div ref={contentRef}>
            {lines.map((line) => (
              <LogRow key={line.eventId} line={line} raw={raw} />
            ))}
            {hasNextPage && (
              <div ref={sentinelRef} className="p-3 text-center text-[12px] text-[var(--nzr-muted)] border-t border-[var(--border-soft)]">
                {isFetchingNextPage ? "Loading more logs…" : ""}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
